import * as React from "react";
import {AlarmsGlobalMonitorTexts, AppTranslations} from "../../../components/Utils/translations/AppTranslations";
import {
    AlarmSeverity,
    AlarmSeverityCount,
    AlarmStatus,
    AlarmStatusCount,
    JSONAlarmCategoryStatistics,
} from "../data/AlarmsMonitorData";
import {AlarmsMonitorDataMapper} from "../utils/AlarmsMonitorDataMapper";
import {MonitorWidget} from "./MonitorWidget";
import {Buttons} from "../../../components/buttons/Buttons";
import {DateRangePicker} from "@vebox/etui-core";
import {ChipButton} from "../../../components/buttons/ChipButton";
import {getChipColor} from "../../../equipment/details/alarms/subfilters/Commons";

declare var AppTranslations: AppTranslations;
declare var LOCALE: string;

interface GlobalMonitorPageProps {
    texts: AlarmsGlobalMonitorTexts;
    alarmStatuses: AlarmStatusCount[];
    data: JSONAlarmCategoryStatistics[];
    selectedSeverity: AlarmSeverity;
    selectedAlarmStatus: AlarmStatus;
    onAlarmElementClick: (alarmId: number, equipmentId: number) => void;
    filteredAlarmTypesMap: Map<number, number[]>;
    onOpenCategoryAlarmTypesFilter: (category: JSONAlarmCategoryStatistics) => void;
    onGlobalFiltersClick: () => void;
    onExportClick: () => void;
    onAlarmStatusPageChange: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
    globalFiltersOpened: boolean;
    openedCategoryId: number;
    openedSeverityGuid: string;
    onAlarmListOpen: (categoryId: number, severityGuid: string, categoryGuid: string) => void;
    onAlarmListClose: () => void;
    onFiltersApply: (fromDate: Date, toDate: Date, severityGuid: string[]) => void;
    filters: string;
    useAsFilter: () => void;
    severities: AlarmSeverityCount[];
    filtersFromDate: Date;
    filtersToDate: Date;
    filtersSeveritiesGuid: string[];
}

export class GlobalMonitorPage extends React.Component<GlobalMonitorPageProps, any> {
    constructor(props) {
        super(props);
        this.state = {
            filtersFromDate: props.filtersFromDate != null ? new Date(props.filtersFromDate) : null,
            filtersToDate: props.filtersToDate != null ? new Date(props.filtersToDate) : null,
            filtersSeveritiesGuid: props.filtersSeveritiesGuid != null ? [...props.filtersSeveritiesGuid] : null,
            startDate: undefined,
            endDate: undefined,
        };
    }

    severitiesInfo = this.props.severities.map(
        (severity) => {
            return severity.severityInfo;
        },
    );

    headerSeverities = this.props.severities.map(
        (severity, index) => {
            let severityClass = "monitor-header-severities__severity";
            if (this.props.selectedSeverity != null
                && AlarmsMonitorDataMapper.toAlarmsSeverityEnum(
                    severity.severityInfo.guid) === this.props.selectedSeverity) {
                severityClass += " selected";
            }
            let severityOpacityStyle = {opacity: 0.2};
            if (this.props.filtersSeveritiesGuid == null
                || this.props.filtersSeveritiesGuid.indexOf(severity.severityInfo.guid) === -1) {
                severityOpacityStyle = {opacity: 1};
            }
            return (
                <div
                    className={severityClass}
                    key={index}
                    data-id={severity.severityInfo.guid}
                    style={severityOpacityStyle}
                >
                    <div
                        className={"monitor-header-severities__severity-color"}
                        style={{backgroundColor: severity.severityInfo.color}}
                    />
                    <p>{severity.severityInfo.name}</p>
                </div>
            );
        },
    );

    public render() {
        const severities = this.props.severities.map(
            (severity, index) => {
                const severityColor = AlarmsMonitorDataMapper.getSeverityColor(
                    this.severitiesInfo,
                    severity.severityInfo.guid,
                );
                return (
                    <ChipButton
                        label={severity.severityInfo.name}
                        key={severity.severityInfo.guid}
                        onChange={
                            // tslint:disable-next-line:jsx-no-lambda
                            () => {
                                this.toggleSeverityAndApply(severity.severityInfo.guid);
                                // this.onFiltersApply();
                            }
                        }
                        checked={this.isSeveritySelected(severity.severityInfo.guid)}
                        color={getChipColor(false, this.isSeveritySelected(severity.severityInfo.guid))}
                        accentColor={severityColor}
                    />
                );
            },
        );

        const tabButtons = this.props.alarmStatuses.map(
            (status, index) => {
                let buttonClass = "second-level-tabbar__button";
                if (this.props.selectedAlarmStatus === status.statusInfo.status) {
                    buttonClass += " selected";
                }
                return (
                    <div
                        key={index}
                        className={buttonClass}
                        data-id={status.statusInfo.statusId}
                        onClick={this.props.onAlarmStatusPageChange}
                    >
                        <p title={status.statusInfo.name + " (" + status.count + ")"}>
                            {status.statusInfo.name}
                        </p>
                        <p className={"count-value"}>({status.count})</p>
                    </div>
                );
            },
        );
        const maxAlarmsCount: Map<string, number> = AlarmsMonitorDataMapper.getMaxCount(
            this.props.data,
            AlarmsMonitorDataMapper.toAlarmStatusCode(this.props.selectedAlarmStatus),
            AlarmsMonitorDataMapper.toAlarmsSeverityGuid(this.props.selectedSeverity),
        );
        const widgets = this.props.data.map(
            (element, index) => {
                return (
                    <MonitorWidget
                        data={element}
                        key={index}
                        onListElementClick={this.props.onAlarmElementClick}
                        onTypesFiltersChange={null}
                        selectedSeverity={this.props.selectedSeverity}
                        selectedAlarmStatus={this.props.selectedAlarmStatus}
                        maxAlarmsCount={maxAlarmsCount}
                        isFilterEnabled={this.props.filteredAlarmTypesMap.get(element.categoryId).length > 0}
                        onFilterOpen={this.props.onOpenCategoryAlarmTypesFilter}
                        openedCategoryId={this.props.openedCategoryId}
                        openedSeverityGuid={this.props.openedSeverityGuid}
                        onAlarmListOpen={this.props.onAlarmListOpen}
                        onAlarmListClose={this.props.onAlarmListClose}
                        filters={this.props.filters}
                        useAsFilter={this.props.useAsFilter}
                    />
                );
            },
        );

        return (
            <div className={"global-monitor-page-content"}>
                {this.props.globalFiltersOpened && <div className={"cover-content-container"}/>}
                <div className={"monitor-tabs"}>
                    <div className={"responsive-tabbar-element"}>
                        <div className={"second-level-tabbar"}>
                            {tabButtons}
                        </div>
                        <div className={"alarm-severities"}>
                            {severities}
                        </div>
                    </div>
                    <div className={"responsive-tabbar-element"}>
                        <div className={"alarm-daterange-picker"}>
                            <DateRangePicker
                                label={`${this.props.texts.from} - ${this.props.texts.to}`}
                                dateFormat={
                                    "DD/MM/YYYY"
                                    /*
                                    TODO: ripristinare l'utilizzo del LocaleDateFormat una volta che la dependency di ETUI
                                        sarà aggiornata. ↓
                                    I18n.getLocaleDateFormat(LOCALE)
                                    */
                                }
                                alignRight={true}
                                onChange={
                                    // tslint:disable-next-line:jsx-no-lambda
                                    (p) => {
                                        this.setState({
                                                filtersFromDate: p.startDate,
                                                filtersToDate: p.endDate,
                                            },
                                            () => this.onFiltersApply()
                                        );
                                    }}
                                styles={{
                                    input: {
                                        pb: "0",
                                        pt: "10px",
                                        width: "unset",
                                        bg: "var(--second-level-tabbar-button-bg)",
                                        color: "var(--second-level-tabbar-active-text-color)"
                                    },
                                    inputLabel: {
                                        color: "var(--second-level-tabbar-text-color)",
                                        textTransform: "uppercase"
                                    }
                                }}
                                startDate={this.state.filtersFromDate}
                                endDate={this.state.filtersToDate}
                            />
                        </div>
                        <div className={"function-buttons"}>
                            {/*<Tooltip
                            placement={"left"}
                            styles={{
                                div: {
                                    my: "auto",
                                    height: "100%",
                                    display: "flex",
                                }
                            }}
                            message={() =>
                                <div className={"monitor-header-severities"}>
                                    <p className={"monitor-header-severities__title"}>{this.props.texts.severity}:</p>
                                    {this.headerSeverities}
                                    {
                                        this.props.filtersToDate != null && this.props.filtersFromDate != null &&
                                        <div className={"filter-descriptions"}>
                                            {this.getFilterDescription()}
                                        </div>
                                    }
                                </div>
                            }
                        >
                            <Buttons.ButtonWithSvg
                                svgName={icons.NOTIFICATION} onClick={(key) => {
                                console.log(key)
                            }}
                            />
                        </Tooltip>*/}
                            <Buttons.ExportToExcel
                                onClick={this.props.onExportClick}
                                label={this.props.texts.export}
                            />
                        </div>
                    </div>
                </div>
                <div className={"monitor-page"}>
                    <div className={"monitor-page__content"}>
                        <div className={"monitor-page__widgets"}>
                            {this.props.selectedAlarmStatus === AlarmStatus.RECENTLY_CLOSED &&
                                <div className={"monitor-header__title"}>
                                    {AppTranslations.alarmsMonitor.texts.closedAlarmsNotVisible2days}
                                </div>
                            }
                            {widgets}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private onFiltersApply() {
        console.log("Applying filters with excluded GUID: " + this.state.filtersSeveritiesGuid);
        this.props.onFiltersApply(
            this.state.filtersFromDate != null ? new Date(this.state.filtersFromDate) : null,
            this.state.filtersToDate != null ? new Date(this.state.filtersToDate) : null,
            this.state.filtersSeveritiesGuid != null ? [...this.state.filtersSeveritiesGuid] : null,
        );
    }

    private areFiltersEmpty() {
        return this.props.filtersToDate == null
            && this.props.filtersFromDate == null
            && (this.props.filtersSeveritiesGuid == null || this.props.filtersSeveritiesGuid.length === 0);
    }

    private getFilterDescription() {
        if (this.props.filtersFromDate == null || this.props.filtersToDate == null) {
            return null;
        }
        return (
            <span>
                {this.props.texts.from + " "}
                <b>{this.props.filtersFromDate.toLocaleDateString() + " "}</b>
                {this.props.texts.to + " "}
                <b>{this.props.filtersToDate.toLocaleDateString()}</b>
            </span>
        );
    }

    private isSeveritySelected(severityGuid: string) {
        return this.state.filtersSeveritiesGuid == null
            || this.state.filtersSeveritiesGuid.indexOf(severityGuid) === -1;
    }

    private toggleSeverityAndApply(severityGuid) {
        // tslint:disable-next-line:radix
        let filteredSeverities = this.state.filtersSeveritiesGuid;
        if (this.isSeveritySelected(severityGuid)) {
            if (filteredSeverities == null) {
                filteredSeverities = [];
            }
            filteredSeverities.push(severityGuid);
        } else {
            filteredSeverities[filteredSeverities.indexOf(severityGuid)] = null;
        }
        this.setState({
                filtersSeveritiesGuid: filteredSeverities.filter(
                    (element) => {
                        return element != null;
                    },
                ),
            }, () => this.onFiltersApply()
        );
    }
}
